import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Form,
  Label,
  Loader,
  Message,
  Modal,
  Table
} from 'semantic-ui-react';
import { I18n } from 'react-redux-i18n';
import moment from 'moment';

import { useAppSelector } from '../../../../hooks/redux/useApp';
import { selectIsFeatureFlagExplicitlyEnabled } from '../../../../modules/featureFlags/featureFlagSelectors';
import { FeatureFlagKeys } from '../../../../modules/featureFlags/featureFlagTypes';
import SystemOnlyBadge from '../../../../components/common/SystemOnlyBadge';
import { isPasskeySupported } from '../../../../helpers/passkeys';
import AddPasskeyModal, { confirmationErrorText } from './AddPasskeyModal';
import {
  AccountPasskey,
  deletePasskey,
  fetchPasskeys,
  renamePasskey
} from './passkeysApi';
import styles from './PasskeysPanel.module.css';

const keyPrefix = 'account.account-security.passkeys';

const formatDate = (value: string | null) =>
  value
    ? moment.utc(value).local().format('D MMM YYYY, h:mm a')
    : I18n.t(`${keyPrefix}.never`);

interface PasskeysPanelProps {
  /** The account whose settings are open. The panel only renders for the signed-in account. */
  accountId?: number;
  hasMfaActive: boolean;
}

/**
 * Your own passkeys: add, rename and remove. Shown to system admins, to other admins once
 * Platform-Security-Passkeys-AllAdmins-UI is on (which is also when the system-only badge goes), and
 * to anyone who already has one, so a passkey is never stranded by the flag going off. Removing one
 * asks for the same confirmation as adding one. Someone else's passkeys are on the Eyes Only tab.
 */
const PasskeysPanel = ({ accountId, hasMfaActive }: PasskeysPanelProps) => {
  const sessionAccountId = useAppSelector((state) => state.session.account?.id);
  const isSystemAdmin = useAppSelector((state) => state.session.isSystemAdmin);
  const openToAllAdmins = useAppSelector((state) =>
    selectIsFeatureFlagExplicitlyEnabled(
      state,
      FeatureFlagKeys.PasskeysAllAdmins
    )
  );
  const isOwn = !!accountId && Number(accountId) === Number(sessionAccountId);
  // Core refuses every passkey call during impersonation.
  const impersonating = useAppSelector((state) => !!state.session.impersonated);
  const mayAdd = isSystemAdmin || openToAllAdmins;

  const [passkeys, setPasskeys] = useState<AccountPasskey[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [renaming, setRenaming] = useState<AccountPasskey | null>(null);
  const [newName, setNewName] = useState('');
  const [renameFailed, setRenameFailed] = useState(false);
  const [removing, setRemoving] = useState<AccountPasskey | null>(null);
  const [removeSecret, setRemoveSecret] = useState('');
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!accountId) {
      return;
    }
    setLoading(true);
    setFailed(false);
    try {
      setPasskeys(await fetchPasskeys(accountId));
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (isOwn && !impersonating) {
      load();
    }
  }, [isOwn, impersonating, load]);

  if (!isOwn) {
    return null;
  }
  if (impersonating && !mayAdd) {
    return null;
  }
  // An admin the flag does not cover still sees passkeys they already have
  if (!impersonating && !mayAdd && (loading || passkeys.length === 0)) {
    return null;
  }

  const handleAdded = () => {
    setAdding(false);
    setNotice(`${keyPrefix}.added`);
    load();
  };

  const openRename = (passkey: AccountPasskey) => {
    setRenaming(passkey);
    setNewName(passkey.name);
    setRenameFailed(false);
  };

  const handleRename = async () => {
    if (!renaming || !accountId) {
      return;
    }
    setBusy(true);
    try {
      await renamePasskey(accountId, renaming.id, newName);
      setRenaming(null);
      setNotice(`${keyPrefix}.renamed`);
      load();
    } catch {
      setRenameFailed(true);
    } finally {
      setBusy(false);
    }
  };

  const openRemove = (passkey: AccountPasskey) => {
    setRemoving(passkey);
    setRemoveSecret('');
    setRemoveError(null);
  };

  const handleRemove = async () => {
    if (!removing || !accountId) {
      return;
    }
    setBusy(true);
    setRemoveError(null);
    try {
      await deletePasskey(
        accountId,
        removing.id,
        hasMfaActive ? { otp: removeSecret } : { password: removeSecret }
      );
      setRemoving(null);
      setNotice(`${keyPrefix}.removed`);
      load();
    } catch (caught) {
      setRemoveError(confirmationErrorText(caught, hasMfaActive));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card fluid className={styles.card}>
      <Card.Content className={styles.content}>
        <h3 className="security-title">
          {I18n.t(`${keyPrefix}.title`)}
          {!openToAllAdmins && <SystemOnlyBadge />}
        </h3>
        <p className={styles.instructions}>
          {I18n.t(`${keyPrefix}.instructions`)}
        </p>

        {notice && (
          <Message positive size="small" onDismiss={() => setNotice(null)}>
            <Message.Content>{I18n.t(notice)}</Message.Content>
          </Message>
        )}
        {failed && (
          <Message negative size="small" role="alert">
            <Message.Content>
              {I18n.t('general.general-error-message')}
            </Message.Content>
          </Message>
        )}

        {impersonating && (
          <Message warning size="small" role="alert">
            <Message.Content>
              {I18n.t(`${keyPrefix}.impersonating`)}
            </Message.Content>
          </Message>
        )}

        {!impersonating && loading && <Loader active inline="centered" />}
        {!impersonating && !loading && passkeys.length === 0 && (
          <p className={styles.empty}>{I18n.t(`${keyPrefix}.none`)}</p>
        )}
        {!impersonating && !loading && passkeys.length > 0 && (
          <Table basic="very" unstackable className={styles.table}>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>
                  {I18n.t(`${keyPrefix}.columns.name`)}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {I18n.t(`${keyPrefix}.columns.added`)}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {I18n.t(`${keyPrefix}.columns.last-used`)}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  {I18n.t(`${keyPrefix}.columns.type`)}
                </Table.HeaderCell>
                <Table.HeaderCell>
                  <span className={styles.srOnly}>
                    {I18n.t(`${keyPrefix}.columns.actions`)}
                  </span>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {passkeys.map((passkey) => (
                <Table.Row key={passkey.id}>
                  <Table.Cell>{passkey.name}</Table.Cell>
                  <Table.Cell>{formatDate(passkey.createdAt)}</Table.Cell>
                  <Table.Cell>{formatDate(passkey.lastUsedAt)}</Table.Cell>
                  <Table.Cell>
                    <Label size="small" className={styles.typeLabel}>
                      {I18n.t(
                        `${keyPrefix}.${passkey.isBackupEligible ? 'synced' : 'device'}`
                      )}
                    </Label>
                  </Table.Cell>
                  <Table.Cell textAlign="right" className={styles.actions}>
                    <Button
                      basic
                      size="small"
                      aria-label={I18n.t(`${keyPrefix}.rename-named`, {
                        name: passkey.name
                      })}
                      onClick={() => openRename(passkey)}
                    >
                      {I18n.t(`${keyPrefix}.rename`)}
                    </Button>
                    <Button
                      basic
                      negative
                      size="small"
                      aria-label={I18n.t(`${keyPrefix}.remove-named`, {
                        name: passkey.name
                      })}
                      onClick={() => openRemove(passkey)}
                    >
                      {I18n.t(`${keyPrefix}.remove`)}
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}

        {!impersonating && mayAdd && isPasskeySupported() && (
          <Button primary onClick={() => setAdding(true)}>
            {I18n.t(`${keyPrefix}.add`)}
          </Button>
        )}
      </Card.Content>

      <AddPasskeyModal
        accountId={accountId}
        hasMfaActive={hasMfaActive}
        open={adding}
        onAdded={handleAdded}
        onClose={() => setAdding(false)}
      />

      <Modal
        className="wizard"
        size="tiny"
        dimmer="inverted"
        closeIcon={!busy}
        closeOnDimmerClick={!busy}
        closeOnEscape={!busy}
        open={!!renaming}
        onClose={() => setRenaming(null)}
      >
        <Modal.Header>
          <h3>{I18n.t(`${keyPrefix}.rename-modal.title`)}</h3>
        </Modal.Header>
        <Modal.Content>
          <Form onSubmit={handleRename} error={renameFailed}>
            <Form.Field>
              <label htmlFor="passkey-rename">
                {I18n.t(`${keyPrefix}.add-modal.name-label`)}
              </label>
              <input
                id="passkey-rename"
                type="text"
                className={styles.input}
                maxLength={100}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </Form.Field>
            {renameFailed && (
              <Message
                error
                role="alert"
                content={I18n.t('general.general-error-message')}
              />
            )}
            <div
              className="button-wrapper align-right"
              style={{ marginTop: '16px' }}
            >
              <Button
                type="button"
                onClick={() => setRenaming(null)}
                disabled={busy}
              >
                {I18n.t(`${keyPrefix}.cancel`)}
              </Button>
              <Button
                primary
                type="submit"
                loading={busy}
                disabled={busy || !newName.trim()}
              >
                {I18n.t(`${keyPrefix}.rename-modal.save`)}
              </Button>
            </div>
          </Form>
        </Modal.Content>
      </Modal>

      <Modal
        className="wizard"
        size="tiny"
        dimmer="inverted"
        closeIcon={!busy}
        closeOnDimmerClick={!busy}
        closeOnEscape={!busy}
        open={!!removing}
        onClose={() => setRemoving(null)}
      >
        <Modal.Header>
          <h3>{I18n.t(`${keyPrefix}.remove-modal.title`)}</h3>
        </Modal.Header>
        <Modal.Content>
          <Form onSubmit={handleRemove} error={!!removeError}>
            <p>
              {I18n.t(`${keyPrefix}.remove-modal.body`, {
                name: removing?.name ?? ''
              })}
            </p>
            <Form.Field>
              <label htmlFor="passkey-remove-confirmation">
                {I18n.t(
                  `${keyPrefix}.add-modal.${hasMfaActive ? 'code-label' : 'password-label'}`
                )}
              </label>
              <input
                id="passkey-remove-confirmation"
                className={styles.input}
                type={hasMfaActive ? 'text' : 'password'}
                inputMode={hasMfaActive ? 'numeric' : undefined}
                autoComplete={
                  hasMfaActive ? 'one-time-code' : 'current-password'
                }
                value={removeSecret}
                onChange={(e) => setRemoveSecret(e.target.value)}
              />
            </Form.Field>
            {removeError && (
              <Message error role="alert" content={removeError} />
            )}
            <div
              className="button-wrapper align-right"
              style={{ marginTop: '16px' }}
            >
              <Button
                type="button"
                onClick={() => setRemoving(null)}
                disabled={busy}
              >
                {I18n.t(`${keyPrefix}.cancel`)}
              </Button>
              <Button
                negative
                type="submit"
                loading={busy}
                disabled={busy || !removeSecret}
              >
                {I18n.t(`${keyPrefix}.remove-modal.confirm`)}
              </Button>
            </div>
          </Form>
        </Modal.Content>
      </Modal>
    </Card>
  );
};

export default PasskeysPanel;
